import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Thermometer, Wind, CloudRain, Droplets, AlertTriangle,
  Coffee, Wrench, Mountain, Fuel, MapPin, Clock,
  ChevronRight, Loader2, Eye
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Helper: WeatherIcon
// ---------------------------------------------------------------------------

export function WeatherIcon({ code, size = 'md' }) {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-xl',
    lg: 'text-3xl',
  };

  const emoji = useMemo(() => {
    if (code === 0) return '\u2600\uFE0F';
    if (code >= 1 && code <= 3) return '\u26C5';
    if (code >= 45 && code <= 48) return '\uD83C\uDF2B\uFE0F';
    if (code >= 51 && code <= 67) return '\uD83C\uDF27\uFE0F';
    if (code >= 71 && code <= 77) return '\uD83C\uDF28\uFE0F';
    if (code >= 80 && code <= 82) return '\uD83C\uDF26\uFE0F';
    if (code >= 95 && code <= 99) return '\u26C8\uFE0F';
    return '\u2600\uFE0F';
  }, [code]);

  return (
    <span className={`${sizeClasses[size]} leading-none`} role="img" aria-label="Wetter">
      {emoji}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 1. VehicleStressIndex
// ---------------------------------------------------------------------------

function getStressColor(score) {
  if (score <= 25) return '#10B981';
  if (score <= 50) return '#F59E0B';
  if (score <= 75) return '#F97316';
  return '#EF4444';
}

function getStressLabel(score) {
  if (score <= 25) return 'Leicht';
  if (score <= 50) return 'Moderat';
  if (score <= 75) return 'Anspruchsvoll';
  return 'Sehr anspruchsvoll';
}

function StressGauge({ score }) {
  const color = getStressColor(score);
  const radius = 54;
  const circumference = Math.PI * radius; // half-circle
  const progress = (score / 100) * circumference;

  return (
    <svg viewBox="0 0 120 70" className="w-full max-w-[180px] mx-auto">
      {/* Background arc */}
      <path
        d="M 6 64 A 54 54 0 0 1 114 64"
        fill="none"
        stroke="currentColor"
        className="text-border"
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* Progress arc */}
      <path
        d="M 6 64 A 54 54 0 0 1 114 64"
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${progress} ${circumference}`}
        className="transition-all duration-700 ease-out"
      />
      {/* Score text */}
      <text
        x="60"
        y="55"
        textAnchor="middle"
        className="fill-foreground"
        style={{ fontSize: '22px', fontFamily: 'Changa, sans-serif', fontWeight: 700 }}
      >
        {score}
      </text>
      <text
        x="60"
        y="68"
        textAnchor="middle"
        className="fill-muted-foreground"
        style={{ fontSize: '7px', fontFamily: 'Manrope, sans-serif' }}
      >
        {getStressLabel(score)}
      </text>
    </svg>
  );
}

const factorLabels = {
  gradient: 'Steigung',
  bends: 'Kurven',
  ascent: 'Anstieg',
  distance: 'Distanz',
  elevation: 'H\u00F6he',
};

function FactorBar({ label, value }) {
  const pct = Math.min(100, Math.max(0, value));
  const color = getStressColor(pct);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-14 shrink-0 text-right">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-6 text-right">{pct}</span>
    </div>
  );
}

export function VehicleStressIndex({ stressData }) {
  if (!stressData) return null;

  const { score, factors, recommendation } = stressData;

  return (
    <Card className="bg-card/90 backdrop-blur-sm border-border">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Eye className="w-4 h-4 text-primary" strokeWidth={1.5} />
          Fahrzeug-Belastungsindex
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <StressGauge score={score} />

        {factors && (
          <div className="space-y-1.5">
            {Object.entries(factors).map(([key, value]) => (
              <FactorBar key={key} label={factorLabels[key] || key} value={value} />
            ))}
          </div>
        )}

        {recommendation && (
          <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-2 mt-2">
            {recommendation}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. WeatherAlongRoute
// ---------------------------------------------------------------------------

function RiskDot({ level }) {
  const colors = {
    low: 'bg-emerald-500',
    medium: 'bg-amber-500',
    high: 'bg-red-500',
  };
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${colors[level] || colors.low} transition-colors duration-300`}
    />
  );
}

function WeatherPoint({ point }) {
  const isHighWarning = point.warning_level === 'high';

  return (
    <div
      className={`
        flex flex-col items-center gap-1 px-2 py-2 rounded-md min-w-[60px] shrink-0
        transition-colors duration-200
        ${isHighWarning ? 'bg-red-500/15 border border-red-500/30' : 'bg-card/60'}
      `}
    >
      <WeatherIcon code={point.weather_code} size="md" />
      <div className="flex items-center gap-0.5">
        <Thermometer className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-medium text-foreground">{point.temperature}\u00B0</span>
      </div>
      <div className="flex items-center gap-0.5">
        <Wind className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-[10px] text-muted-foreground">{point.wind_speed} km/h</span>
      </div>
    </div>
  );
}

export function WeatherAlongRoute({ weatherData, isLoading }) {
  if (isLoading) {
    return (
      <Card className="bg-card/90 backdrop-blur-sm border-border">
        <CardContent className="p-4 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-primary" strokeWidth={1.5} />
          <span className="text-sm text-muted-foreground">Wetterdaten werden geladen...</span>
        </CardContent>
      </Card>
    );
  }

  if (!weatherData || !weatherData.points?.length) return null;

  const { points, summary } = weatherData;

  return (
    <Card className="bg-card/90 backdrop-blur-sm border-border">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CloudRain className="w-4 h-4 text-primary" strokeWidth={1.5} />
          Wetter entlang der Route
          {summary?.overall_risk && (
            <RiskDot level={summary.overall_risk} />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        {/* Horizontal scrollable weather strip */}
        <div className="overflow-x-auto -mx-1 px-1 pb-1">
          <div className="flex gap-2">
            {points.map((point, i) => (
              <WeatherPoint key={i} point={point} />
            ))}
          </div>
        </div>

        {/* Summary warnings */}
        {summary?.warnings?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-t border-border pt-2">
            {summary.warnings.map((warning, i) => (
              <Badge
                key={i}
                variant="outline"
                className="text-[10px] border-amber-500/40 text-amber-400 gap-1"
              >
                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                {warning}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 3. CoolingStops
// ---------------------------------------------------------------------------

const reasonConfig = {
  distance: { icon: '\u2615', color: 'text-blue-400', bg: 'bg-blue-500/15', label: 'Pausenstopp' },
  cooling: { icon: '\uD83D\uDD27', color: 'text-amber-400', bg: 'bg-amber-500/15', label: 'Abk\u00FChlung' },
  scenic: { icon: '\uD83C\uDFD4\uFE0F', color: 'text-emerald-400', bg: 'bg-emerald-500/15', label: 'Aussichtspunkt' },
};

function StopItem({ stop, index, isLast, onSelect }) {
  const config = reasonConfig[stop.reason] || reasonConfig.distance;

  return (
    <button
      onClick={() => onSelect?.(stop)}
      className="flex gap-3 w-full text-left group hover:bg-muted/30 rounded-md p-2 -mx-1 transition-colors duration-200"
    >
      {/* Timeline line + icon */}
      <div className="flex flex-col items-center shrink-0">
        <span
          className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center text-sm
            group-hover:scale-110 transition-transform duration-200`}
        >
          {config.icon}
        </span>
        {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-muted-foreground">
            km {stop.km_from_start?.toFixed(1)}
          </span>
          <Badge variant="outline" className={`text-[10px] ${config.color} border-current/30`}>
            {config.label}
          </Badge>
        </div>
        <p className="text-sm font-medium text-foreground mt-0.5 truncate">
          {stop.name}
        </p>
        {stop.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {stop.description}
          </p>
        )}
        <ChevronRight
          className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground
            group-hover:translate-x-0.5 transition-all duration-200 mt-1"
          strokeWidth={1.5}
        />
      </div>
    </button>
  );
}

export function CoolingStops({ stops, onSelectStop }) {
  if (!stops?.length) return null;

  return (
    <Card className="bg-card/90 backdrop-blur-sm border-border">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Coffee className="w-4 h-4 text-primary" strokeWidth={1.5} />
            Empfohlene Stopps
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {stops.length} {stops.length === 1 ? 'Stopp' : 'Stopps'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <ScrollArea className="max-h-[280px]">
          <div className="space-y-0">
            {stops.map((stop, i) => (
              <StopItem
                key={i}
                stop={stop}
                index={i}
                isLast={i === stops.length - 1}
                onSelect={onSelectStop}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 4. FuelStationFilter
// ---------------------------------------------------------------------------

function StationItem({ station, onClick }) {
  return (
    <button
      onClick={() => onClick?.(station)}
      className="w-full text-left flex items-start gap-3 p-2.5 rounded-md
        hover:bg-muted/30 transition-colors duration-200 group"
    >
      <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0
        group-hover:scale-110 transition-transform duration-200">
        <Fuel className="w-4 h-4 text-primary" strokeWidth={1.5} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{station.name}</p>
        {station.brand && (
          <p className="text-xs text-muted-foreground">{station.brand}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
            <MapPin className="w-3 h-3" strokeWidth={1.5} />
            {station.distance?.toFixed(1)} km
          </span>
          {station.fuel_types?.map((type) => (
            <Badge key={type} variant="outline" className="text-[10px] px-1.5 py-0">
              {type}
            </Badge>
          ))}
        </div>
      </div>

      <ChevronRight
        className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground shrink-0 mt-2
          group-hover:translate-x-0.5 transition-all duration-200"
        strokeWidth={1.5}
      />
    </button>
  );
}

export function FuelStationFilter({ onSearch, stations, isLoading }) {
  const [fuelType, setFuelType] = useState('Alle');
  const [radius, setRadius] = useState([10]);

  const handleSearch = () => {
    onSearch?.({ fuel_type: fuelType, radius: radius[0] });
  };

  const filteredStations = useMemo(() => {
    if (!stations) return [];
    if (fuelType === 'Alle') return stations;
    return stations.filter((s) =>
      s.fuel_types?.some((t) => t.toLowerCase().includes(fuelType.toLowerCase()))
    );
  }, [stations, fuelType]);

  return (
    <Card className="bg-card/90 backdrop-blur-sm border-border">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Fuel className="w-4 h-4 text-primary" strokeWidth={1.5} />
          Tankstellen
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        {/* Filters */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <Select value={fuelType} onValueChange={setFuelType}>
              <SelectTrigger className="h-9 text-xs flex-1">
                <SelectValue placeholder="Kraftstoff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Alle">Alle Kraftstoffe</SelectItem>
                <SelectItem value="E5">Super E5</SelectItem>
                <SelectItem value="Super Plus">Super Plus</SelectItem>
                <SelectItem value="Diesel">Diesel</SelectItem>
              </SelectContent>
            </Select>

            <Button
              size="sm"
              className="h-9 px-3 text-xs active:scale-95 transition-transform"
              onClick={handleSearch}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
              ) : (
                'Suchen'
              )}
            </Button>
          </div>

          {/* Radius slider */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Suchradius</span>
              <span className="text-xs font-mono text-foreground">{radius[0]} km</span>
            </div>
            <Slider
              value={radius}
              onValueChange={setRadius}
              min={1}
              max={20}
              step={1}
              className="w-full"
            />
          </div>
        </div>

        {/* Results */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 className="w-4 h-4 animate-spin text-primary" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Suche Tankstellen...</span>
          </div>
        )}

        {!isLoading && filteredStations.length > 0 && (
          <ScrollArea className="max-h-[220px]">
            <div className="space-y-0.5">
              {filteredStations.map((station, i) => (
                <StationItem key={i} station={station} onClick={onSearch} />
              ))}
            </div>
          </ScrollArea>
        )}

        {!isLoading && stations && filteredStations.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            Keine Tankstellen gefunden.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
